import { fixtureApi, fixtureMode } from './fixtures';
import { IEquityPayment, ILendingCustomer } from './types';

/** Ceviant's payment SDK (merchant-collection-web-sdk), which takes the equity contribution. */
interface CeviantPayConfig {
  publicKey: string;
  mode: 'DEV' | 'PROD';
  amount: number;
  currency: string;
  customerId: string;
  customerEmail: string;
  customerPhone?: number;
  metadata?: Record<string, string>;
  onSuccess: (reference: string) => void;
  onClose?: () => void;
}

declare global {
  interface Window {
    ceviantPay?: (config: CeviantPayConfig) => void;
  }
}

const SCRIPT = {
  TEST: 'https://webpayment.uat.ceviant.cloud/sdk.js',
  PROD: 'https://webpayment.ceviant.cloud/sdk.js',
};

let loading: Promise<void> | null = null;

const load = (mode: 'TEST' | 'PROD') => {
  if (window.ceviantPay) {
    return Promise.resolve();
  }
  if (!loading) {
    loading = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT[mode];
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        loading = null;
        reject(new Error('The payment service could not be loaded'));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
};

/**
 * Opens the payment SDK for the equity: Ceviant Finance's key, the amount due and the metadata that tags the payment
 * with the application, all exactly as the session gives them. The payment only counts once the gateway confirms
 * it to the backend; onPaid just means the customer finished paying.
 */
export const payEquity = async (args: {
  mode: 'TEST' | 'PROD';
  applicationReference: string;
  equity: IEquityPayment;
  customer?: ILendingCustomer;
  onPaid: (reference: string) => void;
  onClose: () => void;
}) => {
  const { mode, applicationReference, equity, customer, onPaid, onClose } = args;
  if (fixtureMode()) {
    return fixtureApi.pay(onPaid);
  }
  if (!equity.publicKey) {
    throw new Error('Payment is not available yet. Please try again shortly.');
  }
  await load(mode);
  if (!window.ceviantPay) {
    throw new Error('The payment service could not be loaded');
  }
  const phone = customer?.phoneNumber ? Number(customer.phoneNumber.replace(/\D/g, '')) : undefined;
  window.ceviantPay({
    publicKey: equity.publicKey,
    mode: mode === 'TEST' ? 'DEV' : 'PROD',
    amount: equity.amount,
    currency: equity.currency,
    customerId: applicationReference,
    customerEmail: customer?.email ?? '',
    customerPhone: phone && !Number.isNaN(phone) ? phone : undefined,
    metadata: equity.metadata,
    onSuccess: onPaid,
    onClose,
  });
};
